import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CookieConsent from './components/ui/CookieConsent';

// Marketing pages (eagerly loaded — these are the landing pages)
import HomePage from './components/marketing/HomePage';
import FaqPage from './components/marketing/FaqPage';
import CalculatorPage from './components/marketing/CalculatorPage';
import BlogPage from './components/marketing/BlogPage';
import BlogPost from './components/marketing/BlogPost';
import AffiliatePage from './components/marketing/AffiliatePage';
import AffiliateLanding from './components/marketing/AffiliateLanding';
import PartnerLearnPage from './components/marketing/PartnerLearnPage';
import PrivacyPage from './components/marketing/PrivacyPage';
import TermsPage from './components/marketing/TermsPage';
import UnsubscribePage from './components/marketing/UnsubscribePage';

// Lazy-loaded (only downloaded when user navigates to these routes)
const LoginPage = lazy(() => import('./components/auth/LoginPage'));
const ProfilePage = lazy(() => import('./components/auth/ProfilePage'));
const IntakeForm = lazy(() => import('./components/intake/IntakeForm'));
const SchedulePage = lazy(() => import('./components/intake/SchedulePage'));
const DisclosurePage = lazy(() => import('./components/legal/DisclosurePage'));
const AffiliateDisclosurePage = lazy(() => import('./components/legal/AffiliateDisclosurePage'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const SubmissionDetail = lazy(() => import('./components/admin/SubmissionDetail'));
const AdminSettings = lazy(() => import('./components/admin/AdminSettings'));
const UserManagement = lazy(() => import('./components/admin/UserManagement'));
const AffiliateManagement = lazy(() => import('./components/admin/AffiliateManagement'));
const DraftLeads = lazy(() => import('./components/admin/DraftLeads'));
const BlogManager = lazy(() => import('./components/admin/BlogManager'));
const BlogEditor = lazy(() => import('./components/admin/BlogEditor'));
const ClientList = lazy(() => import('./components/admin/ClientList'));
const ClientProfile = lazy(() => import('./components/admin/ClientProfile'));
const EngineerDashboard = lazy(() => import('./components/engineer/EngineerDashboard'));
const EngineerReview = lazy(() => import('./components/engineer/EngineerReview'));
const FieldDashboard = lazy(() => import('./components/field/FieldDashboard'));
const FieldJobDetail = lazy(() => import('./components/field/FieldJobDetail'));

function Loading() {
  return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f8fa', color: '#4E636B', fontSize: 14 }}>Loading...</div>;
}

function NotFound() {
  return (
    <div style={{ minHeight: '100vh', background: '#f7f8fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, fontWeight: 700, color: '#19335D', marginBottom: 12 }}>404</div>
        <p style={{ fontSize: 15, color: '#4E636B', marginBottom: 20 }}>Page not found.</p>
        <a href="/" style={{ color: '#19335D', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
          &larr; Go to Dashboard
        </a>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/partners" element={<AffiliatePage />} />
          <Route path="/partners/learn" element={<PartnerLearnPage />} />
          <Route path="/calculator" element={<CalculatorPage />} />
          <Route path="/start" element={<Navigate to="/intake" replace />} />
          <Route path="/intake" element={<IntakeForm />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/r/:code" element={<AffiliateLanding />} />
          <Route path="/schedule/:token" element={<SchedulePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unsubscribe/:leadId" element={<UnsubscribePage />} />
          <Route path="/unsubscribe/:type/:leadId" element={<UnsubscribePage />} />
          <Route path="/legal/disclosures" element={<DisclosurePage />} />
          <Route path="/legal/affiliates" element={<AffiliateDisclosurePage />} />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={['admin', 'processor']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/submissions/:id"
            element={
              <ProtectedRoute roles={['admin', 'processor']}>
                <SubmissionDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute roles={['admin']}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute roles={['admin']}>
                <UserManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/affiliates"
            element={
              <ProtectedRoute roles={['admin']}>
                <AffiliateManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog"
            element={
              <ProtectedRoute roles={['admin']}>
                <BlogManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog/new"
            element={
              <ProtectedRoute roles={['admin']}>
                <BlogEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blog/:postId"
            element={
              <ProtectedRoute roles={['admin']}>
                <BlogEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leads"
            element={
              <ProtectedRoute roles={['admin', 'processor']}>
                <DraftLeads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients"
            element={
              <ProtectedRoute roles={['admin', 'processor']}>
                <ClientList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/:id"
            element={
              <ProtectedRoute roles={['admin', 'processor']}>
                <ClientProfile />
              </ProtectedRoute>
            }
          />

          {/* Processor routes (same components as admin) */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute roles={['processor']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/submissions/:id"
            element={
              <ProtectedRoute roles={['processor']}>
                <SubmissionDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/clients"
            element={
              <ProtectedRoute roles={['processor']}>
                <ClientList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/clients/:id"
            element={
              <ProtectedRoute roles={['processor']}>
                <ClientProfile />
              </ProtectedRoute>
            }
          />

          {/* Engineer routes */}
          <Route
            path="/engineer"
            element={
              <ProtectedRoute roles={['engineer']}>
                <EngineerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/engineer/review/:id"
            element={
              <ProtectedRoute roles={['engineer']}>
                <EngineerReview />
              </ProtectedRoute>
            }
          />

          {/* ClearSeg Engineer routes */}
          <Route
            path="/field"
            element={
              <ProtectedRoute roles={['field_specialist']}>
                <FieldDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/field/jobs/:id"
            element={
              <ProtectedRoute roles={['field_specialist']}>
                <FieldJobDetail />
              </ProtectedRoute>
            }
          />

          {/* Profile - all authenticated users */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute roles={['admin', 'processor', 'engineer', 'field_specialist']}>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
  );
}
